import { useState, useEffect, useCallback } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { 
  User, Mail, Phone, Shield, FileText, Heart, Gift, Package, 
  Calendar, MapPin, Edit2, Check, X, Loader2, Link2, Unlink,
  Car, Hash, Smartphone, Plus, Trash2, Store, BadgeCheck, Upload, MessageSquare, Send,
  ShoppingCart, Home, CreditCard, Crown, ClipboardList, Info, ExternalLink, ChevronDown, Star, Download, Wallet, Banknote
} from 'lucide-react';
import { Button } from '../components/ui/button';
import { MyAdsTab } from '../components/MyAdsTab';
import WalletTab from '../components/WalletTab';
import AddressesTab from '../components/AddressesTab';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';
import { toast } from 'sonner';
import { useLanguage } from '../config/language';
import { useTheme } from '../config/theme';
import GoogleSignInButton from '../components/GoogleSignInButton';
import AppleSignInButton from '../components/AppleSignInButton';
import DashboardHero from '../components/dashboard/DashboardHero';
import DashboardNav from '../components/dashboard/DashboardNav';
import { useAIChat } from '../config/aiChatContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const VERIFIED_BADGE = "https://customer-assets.emergentagent.com/job_cloudgm-hub/artifacts/mxyyg0c2_verified.png";

// Helper function to get auth token
const getAuthHeaders = () => {
  const token = localStorage.getItem('token');
  return token ? { Authorization: `Bearer ${token}` } : {};
};

export default function Dashboard() {
  const { t, language } = useLanguage();
  const { isDarkMode } = useTheme();
  const { openChat: openAIChat } = useAIChat();
  const navigate = useNavigate();
  const isAr = language === 'ar';
  const [user, setUser] = useState(null);
  const [walletBalance, setWalletBalance] = useState(null);
  const [requests, setRequests] = useState([]);
  const [favorites, setFavorites] = useState([]);
  const [shipments, setShipments] = useState([]);
  const [orders, setOrders] = useState([]);
  const [followingStores, setFollowingStores] = useState([]);
  const [expandedOrderId, setExpandedOrderId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchParams, setSearchParams] = useSearchParams();
  const VALID_TABS = ['profile', 'wallet', 'addresses', 'myads', 'purchase-requests', 'negotiations', 'loyalty', 'favorites', 'following', 'requests', 'shipments'];
  const urlTab = searchParams.get('tab');
  const [activeTab, setActiveTabState] = useState(VALID_TABS.includes(urlTab) ? urlTab : 'profile');
  const setActiveTab = (id) => {
    setActiveTabState(id);
    setSearchParams(id === 'profile' ? {} : { tab: id }, { replace: true });
  };
  useEffect(() => {
    if (VALID_TABS.includes(urlTab) && urlTab !== activeTab) setActiveTabState(urlTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [urlTab]);
  const [unlinkingGoogle, setUnlinkingGoogle] = useState(false);
  
  // My Ads state
  const [myAds, setMyAds] = useState({ phones: [], plates: [], vehicles: [] });
  
  // Negotiations state
  const [negotiations, setNegotiations] = useState([]);
  const [selectedNegotiation, setSelectedNegotiation] = useState(null);
  const [showChatModal, setShowChatModal] = useState(false);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  
  // Purchase Requests state
  const [purchaseRequests, setPurchaseRequests] = useState([]);
  const [showPurchaseModal, setShowPurchaseModal] = useState(false);
  const [purchaseForm, setPurchaseForm] = useState({
    category: '',
    title: '',
    description: '',
    budget_min: '',
    budget_max: '',
    urgency: 'normal',
    specifications: {}
  });
  const [submittingPurchase, setSubmittingPurchase] = useState(false);
  
  // Verification state
  const [verificationData, setVerificationData] = useState({
    full_name: '',
    emirates_id: '',
    id_front_image: null,
    id_back_image: null
  });
  const [verificationLoading, setVerificationLoading] = useState(false);
  
  // Profile editing state
  const [isEditing, setIsEditing] = useState(false);
  const [profileData, setProfileData] = useState({
    gender: '',
    birthDate: '',
    phone: '',
  });
  
  // Phone verification state
  const [phoneVerification, setPhoneVerification] = useState({
    isVerifying: false,
    codeSent: false,
    verificationCode: '',
    phoneToVerify: '',
    isChanging: false
  });

  const fetchServiceRequests = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/service-requests`, {
        headers: getAuthHeaders()
      });
      setRequests(response.data.data || []);
    } catch {
      // Service requests might not exist yet
      setRequests([]);
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchFavorites = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/user/favorites`, {
        headers: getAuthHeaders()
      });
      setFavorites(response.data.data || []);
    } catch {
      // Favorites might not exist yet
      setFavorites([]);
    }
  }, []);

  const fetchShipments = useCallback(async () => {
    try {
      // Fetch orders (which include shipment tracking)
      const ordersResponse = await axios.get(`${BACKEND_URL}/api/customer/orders`, {
        headers: getAuthHeaders()
      });
      if (ordersResponse.data.success) {
        setOrders(ordersResponse.data.orders || []);
      }
      
      // Also fetch legacy shipments if any
      const response = await axios.get(`${BACKEND_URL}/api/user/shipments`, {
        headers: getAuthHeaders()
      });
      setShipments(response.data.data || []);
    } catch {
      // Shipments might not exist yet
      setShipments([]);
      setOrders([]);
    }
  }, []);

  const fetchFollowingStores = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/customer/following-stores`, {
        headers: getAuthHeaders()
      });
      if (response.data.stores) {
        setFollowingStores(response.data.stores);
      }
    } catch {
      setFollowingStores([]);
    }
  }, []);

  const fetchMyAds = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/user/ads`, {
        headers: getAuthHeaders()
      });
      setMyAds(response.data || { phones: [], plates: [], vehicles: [] });
    } catch {
      setMyAds({ phones: [], plates: [], vehicles: [] });
    }
  }, []);

  const fetchNegotiations = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/negotiations`, {
        headers: getAuthHeaders()
      });
      setNegotiations(response.data.data || []);
    } catch {
      setNegotiations([]);
    }
  }, []);

  const fetchPurchaseRequests = useCallback(async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/purchase-requests`, {
        headers: getAuthHeaders()
      });
      setPurchaseRequests(response.data.data || []);
    } catch {
      setPurchaseRequests([]);
    }
  }, []);

  useEffect(() => {
    const userData = localStorage.getItem('user');
    if (userData) {
      const parsedUser = JSON.parse(userData);
      setUser(parsedUser);
      setProfileData({
        gender: parsedUser.gender || '',
        birthDate: parsedUser.birth_date || '',
        phone: parsedUser.phone || '',
      });
      fetchServiceRequests();
      fetchFavorites();
      fetchShipments();
      fetchMyAds();
      fetchNegotiations();
      fetchPurchaseRequests();
      fetchFollowingStores();
      axios.get(`${BACKEND_URL}/api/customer/wallet/balance`, { headers: getAuthHeaders() })
        .then((r) => setWalletBalance(r.data?.balance ?? 0))
        .catch(() => setWalletBalance(0));

      // Cross-platform sync — refetch fresh linked-identities state from the
      // server so any link/unlink done on the MOBILE APP or ADMIN panel is
      // reflected here immediately.
      (async () => {
        try {
          const res = await axios.get(`${BACKEND_URL}/api/auth/me`, { headers: getAuthHeaders() });
          if (res?.data) {
            const merged = { ...parsedUser, ...res.data };
            setUser(merged);
            localStorage.setItem('user', JSON.stringify(merged));
          }
        } catch (_) {
          // Silent — offline or 401 handled by axios interceptor
        }
      })();
    }
  }, [fetchServiceRequests, fetchFavorites, fetchShipments, fetchMyAds, fetchFollowingStores]);

  const handleSendVerificationCode = async () => {
    if (!profileData.phone || profileData.phone.length < 9) {
      toast.error('يرجى إدخال رقم هاتف صحيح');
      return;
    }

    // Add UAE country code
    const fullPhone = `+971${profileData.phone}`;

    setPhoneVerification(prev => ({ ...prev, isVerifying: true }));

    try {
      await axios.post(`${BACKEND_URL}/api/auth/send-phone-verification`, {
        phone: fullPhone
      }, {
        headers: getAuthHeaders()
      });
      
      setPhoneVerification(prev => ({ 
        ...prev, 
        codeSent: true, 
        isVerifying: false,
        phoneToVerify: fullPhone
      }));
      toast.success('تم إرسال رمز التحقق إلى هاتفك');
    } catch {
      toast.error('فشل في إرسال رمز التحقق');
      setPhoneVerification(prev => ({ ...prev, isVerifying: false }));
    }
  };

  const handleVerifyPhone = async () => {
    if (!phoneVerification.verificationCode || phoneVerification.verificationCode.length !== 6) {
      toast.error('يرجى إدخال رمز التحقق المكون من 6 أرقام');
      return;
    }

    setPhoneVerification(prev => ({ ...prev, isVerifying: true }));

    try {
      await axios.post(`${BACKEND_URL}/api/auth/verify-phone`, {
        phone: phoneVerification.phoneToVerify,
        code: phoneVerification.verificationCode
      }, {
        headers: getAuthHeaders()
      });
      
      // Update user data
      const updatedUser = { ...user, phone: phoneVerification.phoneToVerify, phone_verified: true };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      
      setPhoneVerification({
        isVerifying: false,
        codeSent: false,
        verificationCode: '',
        phoneToVerify: '',
        isChanging: false
      });
      
      toast.success('تم التحقق من رقم الهاتف بنجاح');
    } catch {
      toast.error('رمز التحقق غير صحيح');
      setPhoneVerification(prev => ({ ...prev, isVerifying: false }));
    }
  };

  const handleSaveProfile = async () => {
    try {
      const response = await axios.put(`${BACKEND_URL}/api/user/profile`, {
        gender: profileData.gender,
        birth_date: profileData.birthDate,
      }, {
        headers: getAuthHeaders()
      });
      
      const updatedUser = { ...user, ...response.data };
      setUser(updatedUser);
      localStorage.setItem('user', JSON.stringify(updatedUser));
      setIsEditing(false);
      toast.success('تم حفظ التغييرات بنجاح');
    } catch (error) {
      toast.error('فشل في حفظ التغييرات');
    }
  };

  const allAds = [...(myAds.phones || []), ...(myAds.plates || []), ...(myAds.vehicles || [])];
  const liveAds = allAds.filter((a) => !['deleted', 'rejected', 'expired'].includes(a.status));
  const pendingPayAds = allAds.filter((a) => a.status === 'pending_payment');
  const expiringAds = allAds.filter((a) => {
    if (a.status !== 'active' || !a.expires_at) return false;
    const left = (new Date(a.expires_at) - Date.now()) / 86400000;
    return left >= 0 && left <= 5;
  });

  const tabs = [
    { id: 'profile', label: isAr ? 'الملف الشخصي' : 'Profile', icon: User },
    { id: 'wallet', label: isAr ? 'المحفظة والمدفوعات' : 'Wallet & Payments', icon: Wallet },
    { id: 'addresses', label: isAr ? 'العناوين المحفوظة' : 'Saved Addresses', icon: MapPin },
    { id: 'myads', label: isAr ? 'إعلاناتي' : 'My Ads', icon: Store, count: liveAds.length },
    { id: 'purchase-requests', label: isAr ? 'طلبات الشراء' : 'Purchase Requests', icon: ShoppingCart, count: purchaseRequests.length },
    { id: 'negotiations', label: isAr ? 'المفاوضات' : 'Negotiations', icon: MessageSquare, count: negotiations.length },
    { id: 'loyalty', label: isAr ? 'برنامج الولاء' : 'Loyalty Program', icon: Gift },
    { id: 'favorites', label: isAr ? 'المفضلة' : 'Favorites', icon: Heart, count: favorites.length },
    { id: 'following', label: isAr ? 'المتاجر المتابعة' : 'Following Stores', icon: Store, count: followingStores.length },
    { id: 'requests', label: isAr ? 'طلبات الخدمات' : 'Service Requests', icon: FileText, count: requests.length },
    { id: 'shipments', label: isAr ? 'طلباتي' : 'My Orders', icon: Package, count: orders.length + shipments.length },
  ];

  const heroStats = {
    ads: liveAds.length,
    wallet: walletBalance === null ? '—' : Number(walletBalance).toLocaleString(),
    points: Number(user?.loyalty_points || 0).toLocaleString(),
    orders: orders.length + shipments.length,
  };
  const heroAlerts = [
    pendingPayAds.length > 0 && { id: 'pending-pay', icon: 'pay', tone: 'amber', onClick: () => setActiveTab('myads'), label: isAr ? `${pendingPayAds.length} إعلان بانتظار الدفع` : `${pendingPayAds.length} ad(s) awaiting payment` },
    expiringAds.length > 0 && { id: 'expiring', icon: 'expire', tone: 'red', onClick: () => setActiveTab('myads'), label: isAr ? `${expiringAds.length} إعلان يوشك على الانتهاء` : `${expiringAds.length} ad(s) expiring soon` },
    user && !user.is_verified && { id: 'verify', icon: 'verify', tone: 'sky', onClick: () => setActiveTab('profile'), label: isAr ? 'وثّق حسابك للحصول على شارة التوثيق' : 'Verify your account to get the badge' },
  ].filter(Boolean);

  const purchaseCategories = [
    { id: 'phone_numbers', label: isAr ? 'أرقام هواتف' : 'Phone Numbers', icon: Smartphone },
    { id: 'car_plates', label: isAr ? 'لوحات سيارات' : 'Car Plates', icon: CreditCard },
    { id: 'vehicles', label: isAr ? 'مركبات' : 'Vehicles', icon: Car },
    { id: 'real_estate', label: isAr ? 'عقارات' : 'Real Estate', icon: Home },
  ];

  const handleSubmitPurchaseRequest = async () => {
    if (!purchaseForm.category || !purchaseForm.title || !purchaseForm.budget_max) {
      toast.error(isAr ? 'يرجى ملء جميع الحقول المطلوبة' : 'Please fill all required fields');
      return;
    }
    
    setSubmittingPurchase(true);
    try {
      await axios.post(`${BACKEND_URL}/api/purchase-requests`, purchaseForm, {
        headers: getAuthHeaders()
      });
      toast.success(isAr ? 'تم إرسال طلب الشراء بنجاح!' : 'Purchase request submitted!');
      setShowPurchaseModal(false);
      setPurchaseForm({
        category: '',
        title: '',
        description: '',
        budget_min: '',
        budget_max: '',
        urgency: 'normal',
        specifications: {}
      });
      fetchPurchaseRequests();
    } catch (error) {
      toast.error(isAr ? 'فشل في إرسال الطلب' : 'Failed to submit request');
    } finally {
      setSubmittingPurchase(false);
    }
  };

  const handleCancelPurchaseRequest = async (requestId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/purchase-requests/${requestId}`, {
        headers: getAuthHeaders()
      });
      toast.success(isAr ? 'تم إلغاء الطلب' : 'Request cancelled');
      fetchPurchaseRequests();
    } catch {
      toast.error(isAr ? 'فشل في إلغاء الطلب' : 'Failed to cancel request');
    }
  };

  const getCategoryInfo = (categoryId) => {
    return purchaseCategories.find(c => c.id === categoryId) || { label: categoryId, icon: Package };
  };

  const getUrgencyBadge = (urgency) => {
    const styles = {
      urgent: 'bg-red-100 text-red-800',
      normal: 'bg-blue-100 text-blue-800',
      flexible: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      urgent: isAr ? 'عاجل' : 'Urgent',
      normal: isAr ? 'عادي' : 'Normal',
      flexible: isAr ? 'مرن' : 'Flexible'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[urgency] || styles.normal}`}>
        {labels[urgency] || urgency}
      </span>
    );
  };

  const getPurchaseStatusBadge = (status) => {
    const styles = {
      active: 'bg-green-100 text-green-800',
      fulfilled: 'bg-blue-100 text-blue-800',
      cancelled: 'bg-gray-100 text-gray-800'
    };
    const labels = {
      active: isAr ? 'نشط' : 'Active',
      fulfilled: isAr ? 'تم التنفيذ' : 'Fulfilled',
      cancelled: isAr ? 'ملغي' : 'Cancelled'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.active}`}>
        {labels[status] || status}
      </span>
    );
  };

  // Send message in negotiation chat
  const handleSendMessage = async () => {
    if (!newMessage.trim() || !selectedNegotiation) return;
    
    setSendingMessage(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/negotiations/${selectedNegotiation.id}/messages`,
        { message: newMessage },
        { headers: getAuthHeaders() }
      );
      
      // Refresh negotiation data
      const response = await axios.get(
        `${BACKEND_URL}/api/negotiations/${selectedNegotiation.id}`,
        { headers: getAuthHeaders() }
      );
      setSelectedNegotiation(response.data);
      setNewMessage('');
      fetchNegotiations();
    } catch (error) {
      toast.error(isAr ? 'فشل في إرسال الرسالة' : 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const openChat = async (negotiation) => {
    try {
      const response = await axios.get(
        `${BACKEND_URL}/api/negotiations/${negotiation.id}`,
        { headers: getAuthHeaders() }
      );
      setSelectedNegotiation(response.data);
      setShowChatModal(true);
    } catch {
      toast.error(isAr ? 'فشل في تحميل المحادثة' : 'Failed to load chat');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-100 text-yellow-800',
      accepted: 'bg-green-100 text-green-800',
      rejected: 'bg-red-100 text-red-800',
      counter_offer: 'bg-blue-100 text-blue-800'
    };
    const labels = {
      pending: isAr ? 'قيد الانتظار' : 'Pending',
      accepted: isAr ? 'مقبول' : 'Accepted',
      rejected: isAr ? 'مرفوض' : 'Rejected',
      counter_offer: isAr ? 'عرض مضاد' : 'Counter Offer'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
        {labels[status] || status}
      </span>
    );
  };

  const handleDeleteAd = async (category, adId) => {
    try {
      await axios.delete(`${BACKEND_URL}/api/user/ads/${category}/${adId}`, {
        headers: getAuthHeaders()
      });
      toast.success(isAr ? 'تم حذف الإعلان' : 'Ad deleted');
      fetchMyAds();
    } catch {
      toast.error(isAr ? 'فشل في حذف الإعلان' : 'Failed to delete ad');
    }
  };

  const handleRequestVerification = async () => {
    if (!verificationData.full_name || !verificationData.emirates_id) {
      toast.error(isAr ? 'يرجى ملء جميع الحقول' : 'Please fill all fields');
      return;
    }
    
    setVerificationLoading(true);
    try {
      await axios.post(`${BACKEND_URL}/api/user/request-verification`, verificationData, {
        headers: getAuthHeaders()
      });
      toast.success(isAr ? 'تم إرسال طلب التوثيق بنجاح' : 'Verification request sent');
    } catch (error) {
      toast.error(error.response?.data?.detail || (isAr ? 'فشل في إرسال الطلب' : 'Failed to send request'));
    } finally {
      setVerificationLoading(false);
    }
  };

  const handleGoogleLinkSuccess = async (data) => {
    // Immediately persist unlinked/linked truth so a subsequent reload
    // shows the correct UI state without waiting on /auth/me.
    const patched = { ...(user || {}), google_linked: true, google_email: data?.google_email, picture: data?.picture || user?.picture };
    setUser(patched);
    localStorage.setItem('user', JSON.stringify(patched));
    try {
      const me = await axios.get(`${BACKEND_URL}/api/auth/me`, { headers: getAuthHeaders() });
      setUser(me.data);
      localStorage.setItem('user', JSON.stringify(me.data));
    } catch (_) {}
    toast.success(isAr ? 'تم ربط حساب Google بنجاح ✓' : 'Google account linked successfully');
    setTimeout(() => { window.location.reload(); }, 700);
  };

  const handleAppleLinkSuccess = async (data) => {
    const patched = { ...(user || {}), apple_linked: true, apple_email: data?.apple_email };
    setUser(patched);
    localStorage.setItem('user', JSON.stringify(patched));
    try {
      const me = await axios.get(`${BACKEND_URL}/api/auth/me`, { headers: getAuthHeaders() });
      setUser(me.data);
      localStorage.setItem('user', JSON.stringify(me.data));
    } catch (_) {}
    toast.success(isAr ? 'تم ربط حساب Apple بنجاح ✓' : 'Apple account linked successfully');
    setTimeout(() => { window.location.reload(); }, 700);
  };

  const [unlinkingApple, setUnlinkingApple] = useState(false);
  const handleUnlinkApple = async () => {
    setUnlinkingApple(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/apple/direct/unlink`,
        {},
        { headers: getAuthHeaders() }
      );
      // Immediately flip local state to unlinked BEFORE reload, so the
      // reloaded page renders the Link button (not the Unlink) even if
      // /auth/me is briefly slow.
      const patched = { ...(user || {}), apple_linked: false, apple_email: null };
      setUser(patched);
      localStorage.setItem('user', JSON.stringify(patched));
      toast.success(isAr ? 'تم إلغاء ربط حساب Apple' : 'Apple account unlinked');
      // Full reload — clears Apple SDK singleton state (nonce/state) and
      // guarantees the "Link" button responds on the next click.
      setTimeout(() => { window.location.reload(); }, 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || (isAr ? 'حدث خطأ' : 'An error occurred'));
      setUnlinkingApple(false);
    }
  };

  const handleUnlinkGoogle = async () => {
    setUnlinkingGoogle(true);
    try {
      await axios.post(
        `${BACKEND_URL}/api/auth/google/direct/unlink`,
        {},
        { headers: getAuthHeaders() }
      );
      // Same trick: persist unlinked state before reload.
      const patched = { ...(user || {}), google_linked: false, google_email: null };
      setUser(patched);
      localStorage.setItem('user', JSON.stringify(patched));
      try { window.google?.accounts?.id?.disableAutoSelect?.(); } catch (_) {}
      toast.success(isAr ? 'تم إلغاء ربط حساب Google' : 'Google account unlinked');
      setTimeout(() => { window.location.reload(); }, 500);
    } catch (error) {
      toast.error(error.response?.data?.detail || (isAr ? 'حدث خطأ' : 'An error occurred'));
      setUnlinkingGoogle(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl text-[#475569]">يجب تسجيل الدخول أولاً / Please login first</p>
        </div>
      </div>
    );
  }

  return (
    <div className={`min-h-screen ${isDarkMode ? 'bg-[#0A0B10]' : 'bg-[#F8FAFC]'}`} data-testid="patient-dashboard">
      <DashboardHero
        user={user}
        isAr={isAr}
        isDarkMode={isDarkMode}
        stats={heroStats}
        alerts={heroAlerts}
        onAskAI={() => openAIChat(isAr ? 'أعطني ملخصاً عن حسابي: إعلاناتي، طلباتي، ورصيد محفظتي' : 'Give me a summary of my account: my ads, orders and wallet balance')}
        onGo={setActiveTab}
        onEditProfile={() => { setActiveTab('profile'); setIsEditing(true); }}
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-8">
        <div className="flex flex-col lg:flex-row gap-6 lg:gap-8">
          <DashboardNav tabs={tabs} activeTab={activeTab} onChange={setActiveTab} isDarkMode={isDarkMode} isAr={isAr} />

          {/* Main Content */}
          <main className="flex-1 min-w-0 animate-fadeIn" key={activeTab}>
            {/* Profile Tab */}
            {activeTab === 'profile' && (
              <div className={`rounded-3xl p-5 sm:p-7 relative overflow-hidden ${isDarkMode ? 'cgm-glass-dark' : 'cgm-glass-light shadow-sm'}`} data-testid="profile-section">
                <div className="flex justify-between items-center mb-6">
                  <h2 className={`text-xl font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>الملف الشخصي</h2>
                  {!isEditing ? (
                    <Button 
                      variant="outline" 
                      onClick={() => setIsEditing(true)}
                      className="flex items-center gap-2"
                    >
                      <Edit2 className="h-4 w-4" />
                      تعديل
                    </Button>
                  ) : (
                    <div className="flex gap-2">
                      <Button onClick={handleSaveProfile} className="bg-[#1E9BD9]">
                        <Check className="h-4 w-4 me-1" /> حفظ
                      </Button>
                      <Button variant="outline" onClick={() => setIsEditing(false)}>
                        <X className="h-4 w-4 me-1" /> إلغاء
                      </Button>
                    </div>
                  )}
                </div>

                <div className="grid md:grid-cols-2 gap-6">
                  {/* Basic Info - Read Only */}
                  <div className="space-y-4">
                    <div>
                      <Label className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#94A3B8]'}`}>الاسم الكامل</Label>
                      <p className={`font-medium mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{user.name}</p>
                    </div>
                    <div>
                      <Label className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#94A3B8]'}`}>البريد الإلكتروني</Label>
                      <p className={`font-medium mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{user.email}</p>
                    </div>
                    <div>
                      <Label className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#94A3B8]'}`}>نوع الحساب</Label>
                      <p className={`font-medium mt-1 capitalize ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{user.role}</p>
                    </div>
                    {user.membership_number && (
                      <>
                        <div>
                          <Label className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#94A3B8]'}`}>{isAr ? 'رقم العضوية' : 'Membership Number'}</Label>
                          <p className={`font-bold text-xl mt-1 tracking-widest font-mono ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{user.membership_number}</p>
                        </div>
                        <div>
                          <Label className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#94A3B8]'}`}>{isAr ? 'مستوى العضوية' : 'Membership Tier'}</Label>
                          <div className="flex items-center gap-3 mt-1">
                            <span className={`w-3 h-3 rounded-full ${
                              user.membership_tier === 'Platinum' ? 'bg-purple-500' :
                              user.membership_tier === 'Gold' ? 'bg-yellow-500' :
                              user.membership_tier === 'Silver' ? 'bg-gray-400' :
                              'bg-blue-500'
                            }`}></span>
                            <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                              {user.membership_tier === 'Blue' ? (isAr ? 'الأزرق' : 'Blue') :
                               user.membership_tier === 'Silver' ? (isAr ? 'الفضي' : 'Silver') :
                               user.membership_tier === 'Gold' ? (isAr ? 'الذهبي' : 'Gold') :
                               user.membership_tier === 'Platinum' ? (isAr ? 'البلاتيني' : 'Platinum') :
                               user.membership_tier}
                            </span>
                            <span className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`}>• {user.loyalty_points || 0} {isAr ? 'نقطة' : 'pts'}</span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>

                  {/* Editable Info */}
                  <div className="space-y-4">
                    {/* Gender */}
                    <div>
                      <Label className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#94A3B8]'}`}>الجنس</Label>
                      {isEditing ? (
                        <select
                          value={profileData.gender}
                          onChange={(e) => setProfileData({ ...profileData, gender: e.target.value })}
                          className={`w-full mt-1 p-2 border rounded-lg focus:ring-2 focus:ring-[#1E9BD9] ${
                            isDarkMode ? 'bg-white/10 border-white/20 text-white' : 'border-[#E2E8F0] text-[#0F172A]'
                          }`}
                        >
                          <option value="">اختر الجنس</option>
                          <option value="male">ذكر</option>
                          <option value="female">أنثى</option>
                        </select>
                      ) : (
                        <p className={`font-medium mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                          {profileData.gender === 'male' ? 'ذكر' : profileData.gender === 'female' ? 'أنثى' : 'غير محدد'}
                        </p>
                      )}
                    </div>

                    {/* Birth Date */}
                    <div>
                      <Label className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#94A3B8]'}`}>تاريخ الميلاد</Label>
                      {isEditing ? (
                        <Input
                          type="date"
                          value={profileData.birthDate}
                          onChange={(e) => setProfileData({ ...profileData, birthDate: e.target.value })}
                          className={`mt-1 ${isDarkMode ? 'bg-white/10 border-white/20 text-white' : ''}`}
                        />
                      ) : (
                        <p className={`font-medium mt-1 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                          {profileData.birthDate || 'غير محدد'}
                        </p>
                      )}
                    </div>

                    {/* Phone with Verification */}
                    <div>
                      <Label className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#94A3B8]'}`}>رقم الهاتف</Label>
                      {user.phone_verified && !phoneVerification.isChanging ? (
                        <div className="flex items-center gap-2 mt-1">
                          <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{user.phone}</p>
                          <span className="text-green-600 text-xs flex items-center gap-1">
                            <Check className="h-3 w-3" /> موثق
                          </span>
                          <button
                            onClick={() => {
                              setPhoneVerification(prev => ({ 
                                ...prev, 
                                isChanging: true,
                                codeSent: false,
                                verificationCode: ''
                              }));
                              setProfileData(prev => ({ ...prev, phone: '' }));
                            }}
                            className="text-[#1E9BD9] text-xs hover:underline flex items-center gap-1 ms-2"
                          >
                            <Edit2 className="h-3 w-3" /> تغيير
                          </button>
                        </div>
                      ) : (
                        <div className="space-y-3 mt-1">
                          {phoneVerification.isChanging && (
                            <div className={`rounded-lg p-2 mb-2 ${isDarkMode ? 'bg-yellow-900/30 border border-yellow-700' : 'bg-yellow-50 border border-yellow-200'}`}>
                              <p className={`text-xs ${isDarkMode ? 'text-yellow-400' : 'text-yellow-700'}`}>سيتم إلغاء توثيق الرقم الحالي وستحتاج للتحقق من الرقم الجديد</p>
                            </div>
                          )}
                          <div className="flex gap-2">
                            {/* Country Code Dropdown */}
                            <div className={`flex items-center rounded-lg px-3 ${isDarkMode ? 'bg-white/10 border border-white/20' : 'bg-[#F8FAFC] border border-[#E2E8F0]'}`}>
                              <img 
                                src="https://flagcdn.com/w20/ae.png" 
                                alt="UAE" 
                                className="w-5 h-auto me-2"
                              />
                              <span className={`font-medium ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>+971</span>
                            </div>
                            <Input
                              type="tel"
                              placeholder="50 123 4567"
                              value={profileData.phone}
                              onChange={(e) => {
                                // Only allow numbers
                                const value = e.target.value.replace(/\D/g, '').slice(0, 9);
                                setProfileData({ ...profileData, phone: value });
                              }}
                              className={`flex-1 ${isDarkMode ? 'bg-white/10 border-white/20 text-white placeholder:text-gray-500' : ''}`}
                              dir="ltr"
                              maxLength={9}
                            />
                            <Button
                              onClick={handleSendVerificationCode}
                              disabled={phoneVerification.isVerifying || phoneVerification.codeSent || profileData.phone.length < 9}
                              className="bg-[#1E9BD9] whitespace-nowrap"
                            >
                              {phoneVerification.isVerifying ? (
                                <Loader2 className="h-4 w-4 animate-spin" />
                              ) : phoneVerification.codeSent ? (
                                'تم الإرسال'
                              ) : (
                                'إرسال رمز التحقق'
                              )}
                            </Button>
                          </div>
                          
                          {phoneVerification.isChanging && !phoneVerification.codeSent && (
                            <button
                              onClick={() => {
                                setPhoneVerification(prev => ({ 
                                  ...prev, 
                                  isChanging: false,
                                  codeSent: false,
                                  verificationCode: ''
                                }));
                              }}
                              className="text-[#94A3B8] text-xs hover:underline"
                            >
                              إلغاء التغيير
                            </button>
                          )}
                          
                          {phoneVerification.codeSent && (
                            <div className="flex gap-2">
                              <Input
                                type="text"
                                placeholder="أدخل رمز التحقق (6 أرقام)"
                                value={phoneVerification.verificationCode}
                                onChange={(e) => setPhoneVerification({ 
                                  ...phoneVerification, 
                                  verificationCode: e.target.value.replace(/\D/g, '').slice(0, 6)
                                })}
                                className="flex-1"
                                maxLength={6}
                                dir="ltr"
                              />
                              <Button
                                onClick={handleVerifyPhone}
                                disabled={phoneVerification.isVerifying}
                                className="bg-green-600 hover:bg-green-700"
                              >
                                {phoneVerification.isVerifying ? (
                                  <Loader2 className="h-4 w-4 animate-spin" />
                                ) : (
                                  'تحقق'
                                )}
                              </Button>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </div>

                {/* Google Account Linking Section - Merged into Profile */}
                <div className={`mt-8 pt-8 border-t ${isDarkMode ? 'border-white/10' : 'border-[#E2E8F0]'}`}>
                  <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    <Link2 className="h-5 w-5 text-[#1E9BD9]" />
                    {isAr ? 'ربط الحسابات الخارجية' : 'External Account Linking'}
                  </h3>
                  
                  <div className={`rounded-xl p-4 transition-colors ${isDarkMode ? 'border border-white/10 hover:border-[#1E9BD9]' : 'border border-[#E2E8F0] hover:border-[#1E9BD9]'}`}>
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-sm ${isDarkMode ? 'bg-white/10 border border-white/20' : 'bg-white border border-gray-200'}`}>
                          <svg className="w-6 h-6" viewBox="0 0 24 24">
                            <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                            <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                            <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                            <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
                          </svg>
                        </div>
                        <div>
                          <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>Google</h4>
                          {user.google_linked ? (
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                <Check className="h-3 w-3" />
                                {isAr ? 'مرتبط' : 'Linked'}
                              </span>
                              <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>{user.google_email}</span>
                            </div>
                          ) : (
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>
                              {isAr ? 'سجّل دخولك بضغطة واحدة' : 'Sign in with one click'}
                            </p>
                          )}
                        </div>
                      </div>
                      <div>
                        {user.google_linked ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleUnlinkGoogle}
                            disabled={unlinkingGoogle}
                            className="text-red-500 border-red-300 hover:bg-red-50"
                            data-testid="unlink-google-btn"
                          >
                            {unlinkingGoogle ? (
                              <Loader2 className="h-4 w-4 animate-spin me-1" />
                            ) : (
                              <Unlink className="h-4 w-4 me-1" />
                            )}
                            {isAr ? 'إلغاء الربط' : 'Unlink'}
                          </Button>
                        ) : (
                          <div data-testid="link-google-wrapper">
                            <GoogleSignInButton
                              key="google-link-btn"
                              mode="link"
                              text="continue_with"
                              locale={isAr ? 'ar' : 'en'}
                              onSuccess={handleGoogleLinkSuccess}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Apple */}
                  <div className={`rounded-xl p-4 mt-3 transition-colors ${isDarkMode ? 'border border-white/10 hover:border-[#1E9BD9]' : 'border border-[#E2E8F0] hover:border-[#1E9BD9]'}`}>
                    <div className="flex items-center justify-between flex-wrap gap-4">
                      <div className="flex items-center gap-3">
                        <div className={`w-12 h-12 rounded-xl flex items-center justify-center shadow-sm ${isDarkMode ? 'bg-white/10 border border-white/20' : 'bg-black border border-black'}`}>
                          <svg className="w-6 h-6" viewBox="0 0 384 512" fill="#ffffff" aria-hidden="true">
                            <path d="M318.7 268.7c-.2-36.7 16.4-64.4 50-84.8-18.8-26.9-47.2-41.7-84.7-44.6-35.5-2.8-74.3 20.7-88.5 20.7-15 0-49.4-19.7-76.4-19.7C63.3 141.2 4 184.8 4 273.5q0 39.3 14.4 81.2c12.8 36.7 59 126.7 107.2 125.2 25.2-.6 43-17.9 75.8-17.9 31.8 0 48.3 17.9 76.4 17.9 48.6-.7 90.4-82.5 102.6-119.3-65.2-30.7-61.7-90-61.7-91.9zM256.9 106.9c22.9-27.2 21.2-63.2 20.6-77.2-23.4 1.4-50.5 15.6-66.9 34.6-18 20.3-28.6 45.4-26.3 71.5 25.6 2 47.3-14 72.6-28.9z"/>
                          </svg>
                        </div>
                        <div>
                          <h4 className={`font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>Apple</h4>
                          {user.apple_linked ? (
                            <div className="flex items-center gap-2">
                              <span className="inline-flex items-center gap-1 px-2 py-0.5 bg-green-100 text-green-700 text-xs font-medium rounded-full">
                                <Check className="h-3 w-3" />
                                {isAr ? 'مرتبط' : 'Linked'}
                              </span>
                              {user.apple_email && (
                                <span className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>{user.apple_email}</span>
                              )}
                            </div>
                          ) : (
                            <p className={`text-xs ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>
                              {isAr ? 'سجّل دخولك باستخدام Apple ID' : 'Sign in with Apple ID'}
                            </p>
                          )}
                        </div>
                      </div>
                      <div>
                        {user.apple_linked ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={handleUnlinkApple}
                            disabled={unlinkingApple}
                            className="text-red-500 border-red-300 hover:bg-red-50"
                            data-testid="unlink-apple-btn"
                          >
                            {unlinkingApple ? (
                              <Loader2 className="h-4 w-4 animate-spin me-1" />
                            ) : (
                              <Unlink className="h-4 w-4 me-1" />
                            )}
                            {isAr ? 'إلغاء الربط' : 'Unlink'}
                          </Button>
                        ) : (
                          <div data-testid="link-apple-wrapper">
                            <AppleSignInButton
                              key="apple-link-btn"
                              mode="link"
                              locale={isAr ? 'ar' : 'en'}
                              onSuccess={handleAppleLinkSuccess}
                            />
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Account Verification Section - Merged into Profile */}
                <div className={`mt-8 pt-8 border-t ${isDarkMode ? 'border-white/10' : 'border-[#E2E8F0]'}`}>
                  <h3 className={`text-lg font-bold mb-4 flex items-center gap-2 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                    <BadgeCheck className="h-5 w-5 text-[#1E9BD9]" />
                    {isAr ? 'توثيق الحساب' : 'Account Verification'}
                  </h3>
                  
                  {user.is_verified ? (
                    <div className="bg-green-50 border border-green-200 rounded-xl p-4 flex items-center gap-4">
                      <img src={VERIFIED_BADGE} alt="Verified" className="w-12 h-12" />
                      <div>
                        <h4 className="font-bold text-green-700">
                          {isAr ? 'حسابك موثق!' : 'Your Account is Verified!'}
                        </h4>
                        <p className="text-sm text-green-600">
                          {isAr ? 'تم التحقق من هويتك بنجاح' : 'Your identity has been verified successfully'}
                        </p>
                      </div>
                    </div>
                  ) : user.verification_status === 'pending' ? (
                    <div className="bg-yellow-50 border border-yellow-200 rounded-xl p-4 flex items-center gap-4">
                      <Loader2 className="h-10 w-10 text-yellow-500 animate-spin" />
                      <div>
                        <h4 className="font-bold text-yellow-700">
                          {isAr ? 'طلب التوثيق قيد المراجعة' : 'Verification Request Pending'}
                        </h4>
                        <p className="text-sm text-yellow-600">
                          {isAr ? 'سيتم مراجعة طلبك خلال 24-48 ساعة' : 'Your request will be reviewed within 24-48 hours'}
                        </p>
                      </div>
                    </div>
                  ) : (
                    <div className="border border-[#E2E8F0] rounded-xl p-4">
                      <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 mb-4">
                        <p className="text-sm text-blue-700">
                          {isAr 
                            ? 'وثّق حسابك للحصول على علامة التوثيق الزرقاء وثقة أكبر من المشترين'
                            : 'Verify your account to get the blue badge and greater trust from buyers'}
                        </p>
                      </div>
                      
                      <div className="grid md:grid-cols-2 gap-4 mb-4">
                        <div>
                          <Label className="text-sm">{isAr ? 'الاسم الكامل (كما في الهوية)' : 'Full Name (as on ID)'}</Label>
                          <Input
                            value={verificationData.full_name}
                            onChange={(e) => setVerificationData({...verificationData, full_name: e.target.value})}
                            placeholder={isAr ? 'أدخل اسمك الكامل' : 'Enter your full name'}
                            className="mt-1"
                          />
                        </div>
                        <div>
                          <Label className="text-sm">{isAr ? 'رقم الهوية الإماراتية' : 'Emirates ID Number'}</Label>
                          <Input
                            value={verificationData.emirates_id}
                            onChange={(e) => setVerificationData({...verificationData, emirates_id: e.target.value})}
                            placeholder="784-XXXX-XXXXXXX-X"
                            className="mt-1"
                          />
                        </div>
                      </div>
                      
                      <Button 
                        onClick={handleRequestVerification}
                        disabled={verificationLoading}
                        className="bg-[#1E9BD9]"
                        size="sm"
                      >
                        {verificationLoading ? (
                          <Loader2 className="h-4 w-4 animate-spin me-2" />
                        ) : (
                          <BadgeCheck className="h-4 w-4 me-2" />
                        )}
                        {isAr ? 'طلب التوثيق' : 'Request Verification'}
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            )}

            {/* Wallet & Payments Tab */}
            {activeTab === 'wallet' && (
              <WalletTab isAr={isAr} isDarkMode={isDarkMode} />
            )}

            {/* Saved Addresses Tab */}
            {activeTab === 'addresses' && (
              <AddressesTab isAr={isAr} isDarkMode={isDarkMode} />
            )}

            {/* Purchase Requests Tab */}
            {activeTab === 'purchase-requests' && (
              <div className={`rounded-3xl p-5 sm:p-7 relative overflow-hidden ${isDarkMode ? 'cgm-glass-dark' : 'cgm-glass-light shadow-sm'}`} data-testid="purchase-requests-section">
                <div className="flex items-center justify-between mb-6">
                  <div>
                    <h2 className={`text-2xl font-bold mb-2 flex items-center ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                      <ShoppingCart className="h-7 w-7 me-3 text-[#10B981]" />
                      {isAr ? 'طلبات الشراء' : 'Purchase Requests'}
                    </h2>
                    <p className={isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}>
                      {isAr ? 'أخبرنا بما تبحث عنه وسنجده لك' : 'Tell us what you\'re looking for and we\'ll find it'}
                    </p>
                  </div>
                  <Button
                    onClick={() => setShowPurchaseModal(true)}
                    className="bg-[#10B981] hover:bg-[#059669]"
                  >
                    <Plus className="h-4 w-4 me-2" />
                    {isAr ? 'طلب جديد' : 'New Request'}
                  </Button>
                </div>

                {purchaseRequests.length === 0 ? (
                  <div className={`text-center py-12 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-[#F8FAFC]'}`}>
                    <ShoppingCart className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-[#CBD5E1]'}`} />
                    <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>
                      {isAr ? 'لا توجد طلبات شراء' : 'No purchase requests'}
                    </h3>
                    <p className={`text-sm mb-4 ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`}>
                      {isAr ? 'أنشئ طلب شراء جديد وسنساعدك في إيجاد ما تبحث عنه' : 'Create a purchase request and we\'ll help you find what you\'re looking for'}
                    </p>
                    <Button
                      onClick={() => setShowPurchaseModal(true)}
                      className="bg-[#10B981] hover:bg-[#059669]"
                    >
                      <Plus className="h-4 w-4 me-2" />
                      {isAr ? 'إنشاء طلب' : 'Create Request'}
                    </Button>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {purchaseRequests.map((req) => {
                      const catInfo = getCategoryInfo(req.category);
                      const CatIcon = catInfo.icon;
                      return (
                        <div 
                          key={req.id} 
                          className={`border rounded-xl p-4 transition-colors ${
                            req.status === 'cancelled' 
                              ? isDarkMode ? 'border-gray-700 bg-gray-800/50 opacity-60' : 'border-gray-200 bg-gray-50 opacity-60'
                              : isDarkMode ? 'border-white/10 hover:border-[#10B981]' : 'border-[#E2E8F0] hover:border-[#10B981]'
                          }`}
                        >
                          <div className="flex items-start justify-between flex-wrap gap-3">
                            <div className="flex gap-4">
                              <div className="w-12 h-12 bg-[#10B981]/10 rounded-xl flex items-center justify-center flex-shrink-0">
                                <CatIcon className="h-6 w-6 text-[#10B981]" />
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center gap-3 mb-1">
                                  <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>{req.title}</h3>
                                  {getPurchaseStatusBadge(req.status)}
                                  {getUrgencyBadge(req.urgency)}
                                </div>
                                <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>{catInfo.label}</p>
                                {req.description && (
                                  <p className={`text-sm mb-2 ${isDarkMode ? 'text-gray-300' : 'text-[#475569]'}`}>{req.description}</p>
                                )}
                                <div className="flex items-center gap-4 text-sm">
                                  <span className={isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}>
                                    {isAr ? 'الميزانية:' : 'Budget:'}{' '}
                                    <span className="font-bold text-[#10B981]">
                                      {req.budget_min > 0 ? `${req.budget_min} - ` : ''}{req.budget_max} {isAr ? 'درهم' : 'AED'}
                                    </span>
                                  </span>
                                  <span className={isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}>
                                    {new Date(req.created_at).toLocaleDateString('ar-AE')}
                                  </span>
                                </div>
                                {req.responses?.length > 0 && (
                                  <div className="mt-2 flex items-center gap-2 text-sm text-[#10B981]">
                                    <MessageSquare className="h-4 w-4" />
                                    {req.responses.length} {isAr ? 'ردود' : 'responses'}
                                  </div>
                                )}
                              </div>
                            </div>
                            {req.status === 'active' && (
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => handleCancelPurchaseRequest(req.id)}
                                className="text-red-500 border-red-200 hover:bg-red-50"
                              >
                                <X className="h-4 w-4 me-1" />
                                {isAr ? 'إلغاء' : 'Cancel'}
                              </Button>
                            )}
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Negotiations Tab */}
            {activeTab === 'negotiations' && (
              <div className={`rounded-3xl p-5 sm:p-7 relative overflow-hidden ${isDarkMode ? 'cgm-glass-dark' : 'cgm-glass-light shadow-sm'}`} data-testid="negotiations-section">
                <h2 className={`text-2xl font-bold mb-2 flex items-center ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                  <MessageSquare className="h-7 w-7 me-3 text-[#1E9BD9]" />
                  {isAr ? 'المفاوضات' : 'My Negotiations'}
                </h2>
                <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>
                  {isAr ? 'تتبع طلبات التفاوض الخاصة بك وتواصل مع التجار' : 'Track your negotiation requests and chat with sellers'}
                </p>

                {negotiations.length === 0 ? (
                  <div className={`text-center py-12 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-[#F8FAFC]'}`}>
                    <MessageSquare className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-[#CBD5E1]'}`} />
                    <h3 className={`text-lg font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>
                      {isAr ? 'لا توجد مفاوضات حالية' : 'No negotiations yet'}
                    </h3>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`}>
                      {isAr ? 'ابدأ بالتفاوض على المنتجات من السوق' : 'Start negotiating on products from the marketplace'}
                    </p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {negotiations.map((neg) => (
                      <div 
                        key={neg.id} 
                        className={`border rounded-xl p-4 transition-colors ${isDarkMode ? 'border-white/10 hover:border-[#1E9BD9]' : 'border-[#E2E8F0] hover:border-[#1E9BD9]'}`}
                      >
                        <div className="flex items-start justify-between flex-wrap gap-3">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <span className="font-bold text-lg" dir="ltr">{neg.item_number}</span>
                              {getStatusBadge(neg.status)}
                            </div>
                            <div className="flex items-center gap-4 text-sm text-[#64748B] mb-3">
                              <span className="flex items-center gap-1">
                                <Store className="h-4 w-4" />
                                {neg.store_name || (isAr ? 'متجر' : 'Store')}
                              </span>
                              <span>
                                {new Date(neg.created_at).toLocaleDateString('ar-AE')}
                              </span>
                            </div>
                            <div className="flex items-center gap-6">
                              <div>
                                <p className="text-xs text-[#94A3B8]">{isAr ? 'السعر الأصلي' : 'Original'}</p>
                                <p className="font-bold text-[#64748B] line-through">{neg.original_price} درهم</p>
                              </div>
                              <div>
                                <p className="text-xs text-[#94A3B8]">{isAr ? 'عرضك' : 'Your Offer'}</p>
                                <p className="font-bold text-green-600">{neg.offered_price} درهم</p>
                              </div>
                            </div>
                          </div>
                          <div className="flex flex-col gap-2">
                            <Button
                              variant="outline"
                              size="sm"
                              onClick={() => openChat(neg)}
                              className="flex items-center gap-2"
                            >
                              <MessageSquare className="h-4 w-4" />
                              {isAr ? 'فتح المحادثة' : 'Open Chat'}
                              {neg.messages?.length > 1 && (
                                <span className="bg-[#1E9BD9] text-white text-xs px-2 py-0.5 rounded-full">
                                  {neg.messages.length}
                                </span>
                              )}
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Account Linking Tab */}
            {/* Loyalty Program Tab */}
            {activeTab === 'loyalty' && (
              <div className={`rounded-3xl p-5 sm:p-7 relative overflow-hidden ${isDarkMode ? 'cgm-glass-dark' : 'cgm-glass-light shadow-sm'}`} data-testid="loyalty-section">
                <h2 className={`text-xl font-bold mb-6 ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>برنامج الولاء</h2>
                
                <div className="bg-gradient-to-r from-[#1E9BD9] to-[#F5A962] rounded-2xl p-6 text-white mb-6">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-white/80 text-sm">نقاط الولاء الحالية</p>
                      <p className="text-4xl font-bold">{user.loyalty_points || 0}</p>
                    </div>
                    <Gift className="h-16 w-16 text-white/30" />
                  </div>
                  <div className="mt-4 pt-4 border-t border-white/20">
                    <p className="text-sm text-white/80">المستوى: <span className="font-bold text-white">{user.loyalty_tier || 'برونزي'}</span></p>
                  </div>
                </div>

                <div className="space-y-4">
                  <h3 className={`font-bold ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>كيف تكسب النقاط؟</h3>
                  <div className="grid md:grid-cols-3 gap-4">
                    <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-[#F8FAFC]'}`}>
                      <p className="text-2xl font-bold text-[#1E9BD9]">1 نقطة</p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#475569]'}`}>لكل 10 درهم تنفقها</p>
                    </div>
                    <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-[#F8FAFC]'}`}>
                      <p className="text-2xl font-bold text-[#1E9BD9]">50 نقطة</p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#475569]'}`}>عند التسجيل لأول مرة</p>
                    </div>
                    <div className={`p-4 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-[#F8FAFC]'}`}>
                      <p className="text-2xl font-bold text-[#1E9BD9]">100 نقطة</p>
                      <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-[#475569]'}`}>عند دعوة صديق</p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Favorites Tab */}
            {activeTab === 'favorites' && (
              <div className={`rounded-3xl p-5 sm:p-7 relative overflow-hidden ${isDarkMode ? 'cgm-glass-dark' : 'cgm-glass-light shadow-sm'}`} data-testid="favorites-section">
                <h2 className={`text-xl font-bold mb-2 flex items-center ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                  <Heart className="h-6 w-6 me-2 text-red-500" />
                  {isAr ? 'المفضلة' : 'Favorites'}
                </h2>
                <p className={`mb-6 ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>{isAr ? 'العناصر التي أضفتها للمفضلة' : 'Items you added to favorites'}</p>
                
                {favorites.length === 0 ? (
                  <div className={`text-center py-12 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-[#F8FAFC]'}`}>
                    <Heart className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-[#CBD5E1]'}`} />
                    <p className={`font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-[#475569]'}`}>{isAr ? 'لا توجد عناصر في المفضلة' : 'No items in favorites'}</p>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`}>{isAr ? 'اضغط على أيقونة القلب لإضافة عناصر للمفضلة' : 'Click the heart icon to add items to favorites'}</p>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {favorites.map((item) => {
                      const itemData = item.item_data || {};
                      const typeIcons = {
                        phone_number: Smartphone,
                        car_plate: CreditCard,
                        vehicle: Car,
                        real_estate: Home
                      };
                      const typeLabels = {
                        phone_number: isAr ? 'رقم هاتف' : 'Phone Number',
                        car_plate: isAr ? 'لوحة سيارة' : 'Car Plate',
                        vehicle: isAr ? 'مركبة' : 'Vehicle',
                        real_estate: isAr ? 'عقار' : 'Real Estate'
                      };
                      const TypeIcon = typeIcons[item.item_type] || Package;
                      
                      return (
                        <div key={item.id} className={`border rounded-xl p-4 hover:shadow-md hover:border-red-200 transition-all group ${isDarkMode ? 'border-white/10' : 'border-[#E2E8F0]'}`}>
                          <div className="flex items-start justify-between mb-3">
                            <div className="flex items-center gap-3">
                              <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-red-500/20' : 'bg-red-50'}`}>
                                <TypeIcon className="h-5 w-5 text-red-500" />
                              </div>
                              <div>
                                <span className="text-xs text-[#94A3B8]">{typeLabels[item.item_type] || item.item_type}</span>
                                <h3 className="font-bold text-[#0F172A]" dir="ltr">
                                  {itemData.number || itemData.title || item.title || 'عنصر'}
                                </h3>
                              </div>
                            </div>
                            <button
                              onClick={async () => {
                                try {
                                  await axios.delete(`${BACKEND_URL}/api/user/favorites/${item.item_type}/${item.item_id}`, {
                                    headers: getAuthHeaders()
                                  });
                                  toast.success(isAr ? 'تمت الإزالة من المفضلة' : 'Removed from favorites');
                                  fetchFavorites();
                                } catch {
                                  toast.error(isAr ? 'حدث خطأ' : 'An error occurred');
                                }
                              }}
                              className="p-2 hover:bg-red-50 rounded-full opacity-0 group-hover:opacity-100 transition-opacity"
                            >
                              <Trash2 className="h-4 w-4 text-red-500" />
                            </button>
                          </div>
                          
                          {itemData.store_name && (
                            <p className="text-sm text-[#64748B] flex items-center gap-1 mb-2">
                              <Store className="h-3 w-3" />
                              {itemData.store_name}
                            </p>
                          )}
                          
                          {(itemData.price || item.price) && (
                            <p className="text-lg font-bold text-green-600">
                              {(itemData.price || item.price)?.toLocaleString()} {isAr ? 'درهم' : 'AED'}
                            </p>
                          )}
                          
                          <p className="text-xs text-[#94A3B8] mt-2">
                            {isAr ? 'أُضيف' : 'Added'}: {new Date(item.created_at).toLocaleDateString('ar-AE')}
                          </p>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}

            {/* Service Requests Tab */}
            {activeTab === 'requests' && (
              <div className={`rounded-3xl p-5 sm:p-7 relative overflow-hidden ${isDarkMode ? 'cgm-glass-dark' : 'cgm-glass-light shadow-sm'}`} data-testid="requests-section">
                <h2 className={`text-xl font-bold mb-6 flex items-center ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                  <FileText className="h-6 w-6 me-2 text-[#1E9BD9]" />
                  تتبع طلبات الخدمات
                </h2>

                {loading ? (
                  <div className="flex justify-center py-12">
                    <Loader2 className="h-8 w-8 animate-spin text-[#1E9BD9]" />
                  </div>
                ) : requests.length === 0 ? (
                  <div className="text-center py-12">
                    <FileText className="h-16 w-16 mx-auto mb-4 text-[#E2E8F0]" />
                    <p className="text-[#475569]">لا توجد طلبات خدمات</p>
                    <p className="text-sm text-[#94A3B8] mt-2">عند طلب خدمة من المساعد الافتراضي، ستظهر طلباتك هنا</p>
                  </div>
                ) : (
                  <div className="space-y-4">
                    {requests.map((request) => (
                      <div 
                        key={request.request_id || request.id} 
                        className={`border rounded-xl p-5 hover:shadow-md transition-shadow ${isDarkMode ? 'border-white/10' : 'border-[#E2E8F0]'}`}
                        data-testid={`request-${request.request_id || request.id}`}
                      >
                        <div className="flex justify-between items-start mb-4">
                          <div>
                            <h3 className={`font-bold text-lg ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                              رقم الطلب: {request.request_number || request.request_id || request.id}
                            </h3>
                            <p className={`text-sm mt-1 ${isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`}>
                              {request.service_info?.title || request.service_name_ar || request.service_type?.replace('_', ' ')}
                            </p>
                          </div>
                          <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                            request.status === 'submitted' ? (isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800') :
                            request.status === 'accepted' ? (isDarkMode ? 'bg-green-900 text-green-200' : 'bg-green-100 text-green-800') :
                            request.status === 'processing' ? (isDarkMode ? 'bg-blue-900 text-blue-200' : 'bg-blue-100 text-blue-800') :
                            request.status === 'pending_payment' ? (isDarkMode ? 'bg-yellow-900 text-yellow-200' : 'bg-yellow-100 text-yellow-800') :
                            request.status === 'final_processing' ? (isDarkMode ? 'bg-orange-900 text-orange-200' : 'bg-orange-100 text-orange-800') :
                            request.status === 'completed' ? (isDarkMode ? 'bg-purple-900 text-purple-200' : 'bg-purple-100 text-purple-800') :
                            (isDarkMode ? 'bg-gray-700 text-gray-200' : 'bg-gray-100 text-gray-800')
                          }`}>
                            {request.status === 'submitted' ? '📤 تم الإرسال' :
                             request.status === 'accepted' ? '✓ تم قبول الطلب' :
                             request.status === 'processing' ? '⏳ جاري المعالجة' :
                             request.status === 'pending_payment' ? '💳 مطلوب دفع' :
                             request.status === 'final_processing' ? '🔄 المعالجة النهائية' :
                             request.status === 'completed' ? '📬 تم الانتهاء والإرسال' : request.status}
                          </span>
                        </div>
                        
                        {/* Status Progress - 6 Steps */}
                        <div className="flex items-center gap-1 mb-3">
                          {/* Step 1: تم الإرسال */}
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                            ['submitted', 'accepted', 'processing', 'pending_payment', 'final_processing', 'completed'].includes(request.status) 
                              ? 'bg-green-500 text-white' : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                          }`}>1</div>
                          <div className={`flex-1 h-1 ${
                            ['accepted', 'processing', 'pending_payment', 'final_processing', 'completed'].includes(request.status) 
                              ? 'bg-green-500' : isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                          }`}></div>
                          
                          {/* Step 2: تم قبول الطلب */}
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                            ['accepted', 'processing', 'pending_payment', 'final_processing', 'completed'].includes(request.status) 
                              ? 'bg-green-500 text-white' : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                          }`}>2</div>
                          <div className={`flex-1 h-1 ${
                            ['processing', 'pending_payment', 'final_processing', 'completed'].includes(request.status) 
                              ? 'bg-green-500' : isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                          }`}></div>
                          
                          {/* Step 3: جاري المعالجة */}
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                            ['processing', 'pending_payment', 'final_processing', 'completed'].includes(request.status) 
                              ? 'bg-green-500 text-white' : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                          }`}>3</div>
                          <div className={`flex-1 h-1 ${
                            ['pending_payment', 'final_processing', 'completed'].includes(request.status) 
                              ? 'bg-green-500' : isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                          }`}></div>
                          
                          {/* Step 4: مطلوب دفع */}
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                            ['pending_payment', 'final_processing', 'completed'].includes(request.status) 
                              ? 'bg-green-500 text-white' : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                          }`}>4</div>
                          <div className={`flex-1 h-1 ${
                            ['final_processing', 'completed'].includes(request.status) 
                              ? 'bg-green-500' : isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                          }`}></div>
                          
                          {/* Step 5: المعالجة النهائية */}
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                            ['final_processing', 'completed'].includes(request.status) 
                              ? 'bg-green-500 text-white' : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                          }`}>5</div>
                          <div className={`flex-1 h-1 ${
                            request.status === 'completed' ? 'bg-green-500' : isDarkMode ? 'bg-gray-700' : 'bg-gray-200'
                          }`}></div>
                          
                          {/* Step 6: تم الانتهاء */}
                          <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-bold ${
                            request.status === 'completed' 
                              ? 'bg-green-500 text-white' : isDarkMode ? 'bg-gray-700 text-gray-400' : 'bg-gray-200 text-gray-500'
                          }`}>6</div>
                        </div>
                        
                        <div className={`flex justify-between text-[9px] mb-3 px-1 ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`}>
                          <span>إرسال</span>
                          <span>قبول</span>
                          <span>معالجة</span>
                          <span>دفع</span>
                          <span>نهائية</span>
                          <span>انتهاء</span>
                        </div>
                        
                        <p className="text-xs text-[#94A3B8] border-t pt-3">
                          تاريخ الطلب: {new Date(request.created_at).toLocaleDateString('ar-AE', { 
                            year: 'numeric', 
                            month: 'long', 
                            day: 'numeric',
                            hour: '2-digit',
                            minute: '2-digit'
                          })}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Shipments Tab */}
            {activeTab === 'shipments' && (
              <div className={`rounded-3xl p-5 sm:p-7 relative overflow-hidden ${isDarkMode ? 'cgm-glass-dark' : 'cgm-glass-light shadow-sm'}`} data-testid="shipments-section">
                <h2 className={`text-xl font-bold mb-6 flex items-center ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                  <Package className="h-6 w-6 me-2 text-[#1E9BD9]" />
                  {isAr ? 'طلباتي' : 'My Orders'}
                </h2>

                {orders.length === 0 && shipments.length === 0 ? (
                  <div className={`text-center py-12 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-[#F8FAFC]'}`}>
                    <Package className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-[#E2E8F0]'}`} />
                    <p className={isDarkMode ? 'text-gray-300' : 'text-[#475569]'}>{isAr ? 'لا توجد طلبات حالية' : 'No orders yet'}</p>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`}>{isAr ? 'ستظهر طلباتك هنا بعد إتمام الشراء' : 'Your orders will appear here after purchase'}</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {/* Display Orders - Compact View */}
                    {orders.map((order) => (
                      <div key={order.order_id} className={`border rounded-xl overflow-hidden ${isDarkMode ? 'border-white/10' : 'border-[#E2E8F0]'}`}>
                        {/* Order Header - Always Visible (Clickable) */}
                        <button
                          onClick={() => setExpandedOrderId(expandedOrderId === order.order_id ? null : order.order_id)}
                          className={`w-full p-4 flex items-center justify-between transition-colors ${
                            isDarkMode ? 'hover:bg-white/5' : 'hover:bg-gray-50'
                          } ${expandedOrderId === order.order_id ? (isDarkMode ? 'bg-white/5' : 'bg-gray-50') : ''}`}
                        >
                          <div className="flex items-center gap-4">
                            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-[#1E9BD9]/20' : 'bg-[#1E9BD9]/10'}`}>
                              <Package className="h-5 w-5 text-[#1E9BD9]" />
                            </div>
                            <div className="text-start">
                              <p className={`font-bold font-mono text-[#1E9BD9]`}>{order.order_id}</p>
                              {order.receipt_number && (
                                <p className={`text-[11px] font-mono ${isDarkMode ? 'text-emerald-400' : 'text-emerald-600'}`} data-testid={`receipt-number-${order.order_id}`}>
                                  {isAr ? 'إيصال: ' : 'Receipt: '}{order.receipt_number}
                                </p>
                              )}
                              <p className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                {new Date(order.created_at).toLocaleDateString('ar-AE', { day: 'numeric', month: 'short', year: 'numeric' })}
                              </p>
                            </div>
                          </div>
                          <div className="flex items-center gap-3">
                            <span className={`px-3 py-1 rounded-full text-xs font-medium ${
                              order.status === 'pending' ? 'bg-yellow-100 text-yellow-800' :
                              order.status === 'confirmed' ? 'bg-blue-100 text-blue-800' :
                              order.status === 'processing' ? 'bg-purple-100 text-purple-800' :
                              order.status === 'shipped' ? 'bg-indigo-100 text-indigo-800' :
                              order.status === 'delivered' ? 'bg-green-100 text-green-800' :
                              order.status === 'cancelled' ? 'bg-red-100 text-red-800' :
                              'bg-gray-100 text-gray-800'
                            }`}>
                              {order.status === 'pending' ? (isAr ? 'قيد الانتظار' : 'Pending') :
                               order.status === 'confirmed' ? (isAr ? 'تم التأكيد' : 'Confirmed') :
                               order.status === 'processing' ? (isAr ? 'قيد المعالجة' : 'Processing') :
                               order.status === 'shipped' ? (isAr ? 'تم الشحن' : 'Shipped') :
                               order.status === 'delivered' ? (isAr ? 'تم التوصيل' : 'Delivered') :
                               order.status === 'cancelled' ? (isAr ? 'ملغي' : 'Cancelled') : order.status}
                            </span>
                            <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                              {order.total} <span className="text-xs text-gray-500">{isAr ? 'د.إ' : 'AED'}</span>
                            </p>
                            <ChevronDown className={`h-5 w-5 transition-transform ${isDarkMode ? 'text-gray-500' : 'text-gray-400'} ${expandedOrderId === order.order_id ? 'rotate-180' : ''}`} />
                          </div>
                        </button>

                        {/* Order Details - Expandable */}
                        {expandedOrderId === order.order_id && (
                          <div className={`p-4 border-t ${isDarkMode ? 'border-white/10 bg-white/5' : 'border-gray-100 bg-gray-50'}`}>
                            {/* Store Info */}
                            {order.items?.[0]?.store_name && (
                              <div className={`mb-4 p-3 rounded-lg flex items-center gap-3 ${isDarkMode ? 'bg-[#1E9BD9]/10' : 'bg-[#1E9BD9]/5'}`}>
                                <Store className="h-5 w-5 text-[#1E9BD9]" />
                                <div>
                                  <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>{isAr ? 'المتجر' : 'Store'}</p>
                                  <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{order.items[0].store_name}</p>
                                </div>
                              </div>
                            )}

                            {/* Order Items */}
                            <div className={`mb-4 p-3 rounded-lg ${isDarkMode ? 'bg-black/30' : 'bg-white'}`}>
                              <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                {isAr ? 'المنتجات' : 'Products'} ({order.items?.length || 0})
                              </p>
                              {order.items?.map((item, idx) => (
                                <div key={idx} className={`flex justify-between items-center py-2 ${idx > 0 ? 'border-t ' + (isDarkMode ? 'border-white/10' : 'border-gray-100') : ''}`}>
                                  <div className="flex items-center gap-3">
                                    {item.image && (
                                      <img src={item.image} alt={item.name} className="w-12 h-12 rounded-lg object-cover" />
                                    )}
                                    <div>
                                      <p className={`font-medium ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{item.name}</p>
                                      <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                        {isAr ? 'الكمية:' : 'Qty:'} {item.quantity} × {item.price} {isAr ? 'د.إ' : 'AED'}
                                      </p>
                                    </div>
                                  </div>
                                  <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>
                                    {item.price * item.quantity} <span className="text-xs text-gray-500">{isAr ? 'د.إ' : 'AED'}</span>
                                  </p>
                                </div>
                              ))}
                            </div>

                            {/* Delivery Info */}
                            {order.delivery_info && (
                              <div className={`mb-4 p-3 rounded-lg ${isDarkMode ? 'bg-black/30' : 'bg-white'}`}>
                                <p className={`text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {isAr ? 'عنوان التوصيل' : 'Delivery Address'}
                                </p>
                                <p className={isDarkMode ? 'text-white' : 'text-gray-800'}>
                                  {order.delivery_info.full_name}
                                </p>
                                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`} dir="ltr">
                                  {order.delivery_info.phone}
                                </p>
                                <p className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                  {order.delivery_info.address}, {order.delivery_info.area}, {order.delivery_info.emirate}
                                </p>
                                {order.delivery_info.notes && (
                                  <p className={`text-sm mt-1 italic ${isDarkMode ? 'text-gray-500' : 'text-gray-500'}`}>
                                    "{order.delivery_info.notes}"
                                  </p>
                                )}
                              </div>
                            )}
                            
                            {/* Order Progress */}
                            <div className={`p-3 rounded-lg ${isDarkMode ? 'bg-black/30' : 'bg-white'}`}>
                              <p className={`text-sm font-medium mb-3 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                                {isAr ? 'حالة الطلب' : 'Order Status'}
                              </p>
                              <div className="flex items-center justify-between relative">
                                <div className={`absolute top-3 left-0 right-0 h-1 ${isDarkMode ? 'bg-white/10' : 'bg-[#E2E8F0]'}`}></div>
                                {['pending', 'confirmed', 'processing', 'shipped', 'delivered'].map((step, idx) => {
                                  const steps = ['pending', 'confirmed', 'processing', 'shipped', 'delivered'];
                                  const currentIdx = steps.indexOf(order.status);
                                  const isActive = idx <= currentIdx;
                                  const isCancelled = order.status === 'cancelled';
                                  return (
                                    <div key={step} className="relative z-10 flex flex-col items-center">
                                      <div className={`w-6 h-6 rounded-full ${
                                        isCancelled ? 'bg-red-500' :
                                        isActive ? 'bg-[#1E9BD9]' : 
                                        isDarkMode ? 'bg-white/20' : 'bg-[#E2E8F0]'
                                      } flex items-center justify-center`}>
                                        {isActive && !isCancelled && <Check className="h-4 w-4 text-white" />}
                                      </div>
                                      <p className={`text-xs mt-2 ${
                                        isCancelled ? 'text-red-500' :
                                        isActive ? 'text-[#1E9BD9]' : 
                                        isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'
                                      }`}>
                                        {step === 'pending' ? (isAr ? 'انتظار' : 'Pending') :
                                         step === 'confirmed' ? (isAr ? 'تأكيد' : 'Confirmed') :
                                         step === 'processing' ? (isAr ? 'تجهيز' : 'Processing') :
                                         step === 'shipped' ? (isAr ? 'شحن' : 'Shipped') : 
                                         (isAr ? 'توصيل' : 'Delivered')}
                                      </p>
                                    </div>
                                  );
                                })}
                              </div>
                            </div>

                            {/* Order Summary */}
                            <div className={`mt-4 pt-4 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-200'}`}>
                              <div className="flex justify-between items-center mb-2">
                                <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{isAr ? 'المجموع الفرعي' : 'Subtotal'}</p>
                                <p className={isDarkMode ? 'text-white' : 'text-gray-800'}>{order.subtotal || order.total} {isAr ? 'د.إ' : 'AED'}</p>
                              </div>
                              <div className="flex justify-between items-center mb-2">
                                <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>{isAr ? 'التوصيل' : 'Delivery'}</p>
                                <p className={order.delivery_fee === 0 ? 'text-green-500' : (isDarkMode ? 'text-white' : 'text-gray-800')}>
                                  {order.delivery_fee === 0 ? (isAr ? 'مجاني' : 'Free') : `${order.delivery_fee} ${isAr ? 'د.إ' : 'AED'}`}
                                </p>
                              </div>
                              <div className="flex justify-between items-center pt-2 border-t border-dashed">
                                <p className={`font-bold ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{isAr ? 'الإجمالي' : 'Total'}</p>
                                <p className="text-xl font-bold text-[#1E9BD9]">{order.total} {isAr ? 'د.إ' : 'AED'}</p>
                              </div>
                              <p className={`text-xs mt-2 ${isDarkMode ? 'text-gray-600' : 'text-gray-400'}`}>
                                {isAr ? 'طريقة الدفع:' : 'Payment:'} {order.payment_method === 'cash_on_delivery' ? (isAr ? 'الدفع عند الاستلام' : 'Cash on Delivery') : order.payment_method}
                              </p>
                              <button
                                type="button"
                                data-testid={`download-invoice-${order.order_id}`}
                                onClick={async () => {
                                  try {
                                    const res = await axios.get(
                                      `${BACKEND_URL}/api/customer/orders/${order.order_id}/invoice`,
                                      { headers: getAuthHeaders() },
                                    );
                                    const html = res.data?.html || '';
                                    if (!html) { toast.error(isAr ? 'تعذّر إنشاء الإيصال' : 'Failed to generate receipt'); return; }
                                    const w = window.open('', '_blank');
                                    if (!w) { toast.error(isAr ? 'المتصفح منع فتح نافذة جديدة' : 'Browser blocked the new window'); return; }
                                    w.document.open(); w.document.write(html); w.document.close();
                                    setTimeout(() => { try { w.focus(); w.print(); } catch (_) {} }, 400);
                                  } catch (e) {
                                    toast.error(e.response?.data?.detail || (isAr ? 'تعذّر إنشاء الإيصال' : 'Failed to generate receipt'));
                                  }
                                }}
                                className="mt-3 w-full py-2 rounded-xl bg-gradient-to-r from-[#1E9BD9] to-[#0EA5E9] text-white font-bold flex items-center justify-center gap-2 hover:opacity-95"
                              >
                                <Download className="h-4 w-4" />
                                {isAr ? 'تحميل الإيصال' : 'Download Receipt'}
                              </button>
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* Following Stores Tab */}
            {activeTab === 'following' && (
              <div className={`rounded-3xl p-5 sm:p-7 relative overflow-hidden ${isDarkMode ? 'cgm-glass-dark' : 'cgm-glass-light shadow-sm'}`} data-testid="following-section">
                <h2 className={`text-xl font-bold mb-6 flex items-center ${isDarkMode ? 'text-white' : 'text-[#0F172A]'}`}>
                  <Store className="h-6 w-6 me-2 text-[#1E9BD9]" />
                  {isAr ? 'المتاجر المتابعة' : 'Following Stores'}
                </h2>

                {followingStores.length === 0 ? (
                  <div className={`text-center py-12 rounded-xl ${isDarkMode ? 'bg-white/5' : 'bg-[#F8FAFC]'}`}>
                    <Store className={`h-16 w-16 mx-auto mb-4 ${isDarkMode ? 'text-gray-600' : 'text-[#E2E8F0]'}`} />
                    <p className={isDarkMode ? 'text-gray-300' : 'text-[#475569]'}>{isAr ? 'لم تتابع أي متجر بعد' : 'You are not following any stores yet'}</p>
                    <p className={`text-sm ${isDarkMode ? 'text-gray-500' : 'text-[#94A3B8]'}`}>{isAr ? 'تابع المتاجر للبقاء على اطلاع بالعروض الجديدة' : 'Follow stores to stay updated with new offers'}</p>
                    <Button 
                      onClick={() => navigate('/products')}
                      className="mt-4 bg-[#1E9BD9] hover:bg-[#1E9BD9]/90"
                    >
                      {isAr ? 'تصفح المتاجر' : 'Browse Stores'}
                    </Button>
                  </div>
                ) : (
                  <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {followingStores.map((store) => (
                      <div 
                        key={store.id} 
                        onClick={() => navigate(`/store/${store.id}`)}
                        className={`border rounded-xl p-4 cursor-pointer transition-all hover:shadow-lg ${isDarkMode ? 'border-white/10 hover:border-[#1E9BD9]/50 bg-white/5' : 'border-[#E2E8F0] hover:border-[#1E9BD9]/50'}`}
                      >
                        <div className="flex items-center gap-3 mb-3">
                          {store.logo ? (
                            <img src={store.logo} alt={store.name} className="w-12 h-12 rounded-full object-cover" />
                          ) : (
                            <div className={`w-12 h-12 rounded-full flex items-center justify-center ${isDarkMode ? 'bg-[#1E9BD9]/20' : 'bg-[#1E9BD9]/10'}`}>
                              <Store className="h-6 w-6 text-[#1E9BD9]" />
                            </div>
                          )}
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-1">
                              <p className={`font-bold truncate ${isDarkMode ? 'text-white' : 'text-gray-800'}`}>{store.name}</p>
                              {store.verified && (
                                <Check className="h-4 w-4 text-[#1E9BD9] flex-shrink-0" />
                              )}
                            </div>
                            <p className={`text-sm truncate ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                              {store.type === 'ecommerce' ? (isAr ? 'متجر إلكتروني' : 'E-commerce') :
                               store.type === 'phone_numbers' ? (isAr ? 'أرقام مميزة' : 'VIP Numbers') :
                               store.type === 'auto' ? (isAr ? 'سيارات' : 'Autos') :
                               store.type === 'real_estate' ? (isAr ? 'عقارات' : 'Real Estate') :
                               store.type}
                            </p>
                          </div>
                        </div>
                        
                        {/* Store Stats */}
                        <div className={`flex items-center justify-between pt-3 border-t ${isDarkMode ? 'border-white/10' : 'border-gray-100'}`}>
                          <div className="flex items-center gap-1">
                            <Star className="h-4 w-4 text-yellow-500 fill-yellow-500" />
                            <span className={`text-sm ${isDarkMode ? 'text-gray-300' : 'text-gray-600'}`}>
                              {store.stats?.rating?.toFixed(1) || '0.0'}
                            </span>
                          </div>
                          <span className={`text-xs ${isDarkMode ? 'text-gray-500' : 'text-gray-400'}`}>
                            {store.stats?.products_count || 0} {isAr ? 'منتج' : 'products'}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )}

            {/* My Ads Tab */}
            {activeTab === 'myads' && (
              <MyAdsTab
                isAr={isAr}
                isDarkMode={isDarkMode}
                myAds={myAds}
                onDelete={handleDeleteAd}
                onRefresh={fetchMyAds}
              />
            )}
          </main>
        </div>
      </div>

      {/* Negotiation Chat Modal */}
      {showChatModal && selectedNegotiation && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-2xl w-full max-w-lg max-h-[80vh] flex flex-col overflow-hidden">
            {/* Chat Header */}
            <div className="bg-gradient-to-r from-[#1E9BD9] to-[#0284C7] text-white p-4">
              <div className="flex items-center justify-between">
                <div>
                  <h3 className="font-bold text-lg" dir="ltr">{selectedNegotiation.item_number}</h3>
                  <p className="text-sm text-white/80 flex items-center gap-2">
                    <Store className="h-4 w-4" />
                    {selectedNegotiation.store_name || (isAr ? 'المتجر' : 'Store')}
                  </p>
                </div>
                <button 
                  onClick={() => setShowChatModal(false)}
                  className="p-2 hover:bg-white/20 rounded-full"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
              <div className="flex items-center gap-4 mt-3 text-sm">
                <div>
                  <span className="text-white/60">{isAr ? 'السعر الأصلي:' : 'Original:'}</span>
                  <span className="font-bold ms-1">{selectedNegotiation.original_price} درهم</span>
                </div>
                <div>
                  <span className="text-white/60">{isAr ? 'عرضك:' : 'Your offer:'}</span>
                  <span className="font-bold ms-1 text-green-300">{selectedNegotiation.offered_price} درهم</span>
                </div>
              </div>
            </div>

            {/* Messages */}
            <div className="flex-1 overflow-y-auto p-4 space-y-3 bg-[#F8FAFC]">
              {selectedNegotiation.messages?.map((msg, idx) => (
                <div 
                  key={msg.id || idx} 
                  className={`flex ${msg.sender_type === 'customer' ? 'justify-end' : 'justify-start'}`}
                >
                  <div className={`max-w-[80%] p-3 rounded-2xl ${
                    msg.sender_type === 'customer'
                      ? 'bg-[#1E9BD9] text-white rounded-br-sm'
                      : isDarkMode 
                        ? 'bg-white/10 border border-white/20 text-white rounded-bl-sm'
                        : 'bg-white border border-[#E2E8F0] text-[#0F172A] rounded-bl-sm'
                  }`}>
                    <p className="text-sm">{msg.message}</p>
                    <p className={`text-xs mt-1 ${
                      msg.sender_type === 'customer' ? 'text-white/60' : 'text-[#94A3B8]'
                    }`}>
                      {new Date(msg.timestamp).toLocaleTimeString('ar-AE', { hour: '2-digit', minute: '2-digit' })}
                    </p>
                  </div>
                </div>
              ))}
            </div>

            {/* Input */}
            <div className="p-4 border-t border-[#E2E8F0] bg-white">
              <div className="flex gap-2">
                <Input
                  value={newMessage}
                  onChange={(e) => setNewMessage(e.target.value)}
                  placeholder={isAr ? 'اكتب رسالتك...' : 'Type your message...'}
                  onKeyPress={(e) => e.key === 'Enter' && handleSendMessage()}
                  className="flex-1"
                />
                <Button
                  onClick={handleSendMessage}
                  disabled={sendingMessage || !newMessage.trim()}
                  className="bg-[#1E9BD9]"
                >
                  {sendingMessage ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <Send className="h-4 w-4" />
                  )}
                </Button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Purchase Request Modal */}
      {showPurchaseModal && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
          <div className={`rounded-2xl w-full max-w-lg max-h-[90vh] overflow-y-auto ${isDarkMode ? 'bg-[#1a1a1a]' : 'bg-white'}`}>
            {/* Header */}
            <div className="bg-gradient-to-r from-[#10B981] to-[#059669] text-white p-4 rounded-t-2xl">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <ShoppingCart className="h-6 w-6" />
                  <h3 className="font-bold text-lg">{isAr ? 'طلب شراء جديد' : 'New Purchase Request'}</h3>
                </div>
                <button 
                  onClick={() => setShowPurchaseModal(false)}
                  className="p-2 hover:bg-white/20 rounded-full"
                >
                  <X className="h-5 w-5" />
                </button>
              </div>
            </div>

            {/* Form */}
            <div className="p-6 space-y-4">
              {/* Category Selection */}
              <div>
                <Label className={`text-base font-semibold mb-3 block ${isDarkMode ? 'text-white' : ''}`}>
                  {isAr ? 'ماذا تبحث عنه؟' : 'What are you looking for?'} *
                </Label>
                <div className="grid grid-cols-2 gap-3">
                  {purchaseCategories.map((cat) => {
                    const Icon = cat.icon;
                    return (
                      <button
                        key={cat.id}
                        onClick={() => setPurchaseForm({...purchaseForm, category: cat.id})}
                        className={`p-4 rounded-xl border-2 transition-all flex flex-col items-center gap-2 ${
                          purchaseForm.category === cat.id
                            ? 'border-[#10B981] bg-[#10B981]/10'
                            : isDarkMode 
                              ? 'border-white/10 hover:border-[#10B981]/50 bg-white/5' 
                              : 'border-[#E2E8F0] hover:border-[#10B981]/50'
                        }`}
                      >
                        <Icon className={`h-6 w-6 ${purchaseForm.category === cat.id ? 'text-[#10B981]' : isDarkMode ? 'text-gray-400' : 'text-[#64748B]'}`} />
                        <span className={`text-sm font-medium ${purchaseForm.category === cat.id ? 'text-[#10B981]' : isDarkMode ? 'text-gray-300' : 'text-[#475569]'}`}>
                          {cat.label}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Title */}
              <div>
                <Label className={isDarkMode ? 'text-white' : ''}>{isAr ? 'عنوان الطلب' : 'Request Title'} *</Label>
                <Input
                  value={purchaseForm.title}
                  onChange={(e) => setPurchaseForm({...purchaseForm, title: e.target.value})}
                  placeholder={isAr ? 'مثال: أبحث عن رقم مميز يبدأ بـ 050' : 'Example: Looking for VIP number starting with 050'}
                  className={`mt-2 ${isDarkMode ? 'bg-white/5 border-white/10 text-white placeholder:text-gray-500' : ''}`}
                />
              </div>

              {/* Description */}
              <div>
                <Label className={isDarkMode ? 'text-white' : ''}>{isAr ? 'تفاصيل إضافية' : 'Additional Details'}</Label>
                <Textarea
                  value={purchaseForm.description}
                  onChange={(e) => setPurchaseForm({...purchaseForm, description: e.target.value})}
                  placeholder={isAr ? 'صف ما تبحث عنه بالتفصيل...' : 'Describe what you\'re looking for in detail...'}
                  rows={3}
                  className={`mt-2 ${isDarkMode ? 'bg-white/5 border-white/10 text-white placeholder:text-gray-500' : ''}`}
                />
              </div>

              {/* Budget */}
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <Label className={isDarkMode ? 'text-white' : ''}>{isAr ? 'الحد الأدنى (درهم)' : 'Min Budget (AED)'}</Label>
                  <Input
                    type="number"
                    value={purchaseForm.budget_min}
                    onChange={(e) => setPurchaseForm({...purchaseForm, budget_min: e.target.value})}
                    placeholder="0"
                    className={`mt-2 ${isDarkMode ? 'bg-white/5 border-white/10 text-white placeholder:text-gray-500' : ''}`}
                    dir="ltr"
                  />
                </div>
                <div>
                  <Label className={isDarkMode ? 'text-white' : ''}>{isAr ? 'الحد الأقصى (درهم)' : 'Max Budget (AED)'} *</Label>
                  <Input
                    type="number"
                    value={purchaseForm.budget_max}
                    onChange={(e) => setPurchaseForm({...purchaseForm, budget_max: e.target.value})}
                    placeholder="10000"
                    className={`mt-2 ${isDarkMode ? 'bg-white/5 border-white/10 text-white placeholder:text-gray-500' : ''}`}
                    dir="ltr"
                  />
                </div>
              </div>

              {/* Urgency */}
              <div>
                <Label className={isDarkMode ? 'text-white' : ''}>{isAr ? 'مدى الاستعجال' : 'Urgency'}</Label>
                <div className="flex gap-3 mt-2">
                  {[
                    { id: 'urgent', label: isAr ? 'عاجل' : 'Urgent', color: 'red' },
                    { id: 'normal', label: isAr ? 'عادي' : 'Normal', color: 'blue' },
                    { id: 'flexible', label: isAr ? 'مرن' : 'Flexible', color: 'gray' }
                  ].map((opt) => (
                    <button
                      key={opt.id}
                      onClick={() => setPurchaseForm({...purchaseForm, urgency: opt.id})}
                      className={`flex-1 py-2 px-4 rounded-lg border-2 text-sm font-medium transition-all ${
                        purchaseForm.urgency === opt.id
                          ? opt.color === 'red' 
                            ? isDarkMode ? 'border-red-500 bg-red-500/20 text-red-400' : 'border-red-500 bg-red-50 text-red-700'
                            : opt.color === 'blue' 
                              ? isDarkMode ? 'border-blue-500 bg-blue-500/20 text-blue-400' : 'border-blue-500 bg-blue-50 text-blue-700'
                              : isDarkMode ? 'border-gray-500 bg-gray-500/20 text-gray-400' : 'border-gray-500 bg-gray-50 text-gray-700'
                          : isDarkMode 
                            ? 'border-white/10 text-gray-400 hover:border-white/20' 
                            : 'border-[#E2E8F0] text-[#64748B] hover:border-gray-300'
                      }`}
                    >
                      {opt.label}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            {/* Actions */}
            <div className="p-6 pt-0 flex gap-3">
              <Button
                onClick={handleSubmitPurchaseRequest}
                disabled={submittingPurchase}
                className="flex-1 bg-[#10B981] hover:bg-[#059669] py-6"
              >
                {submittingPurchase ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Send className="h-4 w-4 me-2" />
                    {isAr ? 'إرسال الطلب' : 'Submit Request'}
                  </>
                )}
              </Button>
              <Button
                variant="outline"
                onClick={() => setShowPurchaseModal(false)}
                className="flex-1 py-6"
              >
                {isAr ? 'إلغاء' : 'Cancel'}
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
